import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useServices } from "../../context/ServiceProvider";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Input,
  Typography,
  Button,
  List,
  Spin,
  Empty,
  Alert,
} from "antd";
import { CopyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useRecents } from "../../hooks/useRecents";
import CreateProjectPageComponentModal from "./CreateProjectPageComponentModal";
import ClonePageComponentModal from "./ClonePageComponentModal";
import { resetBreadcrumbs } from "../../store/slices/navigationSlice";

export default function ProjectPageComponentList() {
  const { projectPageComponent } = useServices();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const { record } = useRecents({ storageKey: "RECENTS::v1", maxItems: 50 });
  const dispatch = useDispatch();

  // Dropdown search state
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = React.useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await projectPageComponent.fetchAllProjectPageComponents();
      setRecords(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [projectPageComponent]);

  // Dropdown search with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    const term = (query || "").trim();
    if (term.length < 3) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data =
          await projectPageComponent.searchProjectPageComponents(term);
        setSearchResults(Array.isArray(data) ? data : []);
        setSearchOpen(true);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, projectPageComponent]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    dispatch(resetBreadcrumbs());
  }, [dispatch]);

  const columns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "Name",
        key: "Name",
        width: "10%",
        ellipsis: true,
        render: (text, rec) => (
          <Link
            to={{ pathname: `/ppc/${rec.Id}` }}
            state={{ crumbLabel: rec.Name }}
            onClick={() => {
              record({
                id: String(rec.Id),
                title: String(text || rec.Name || rec.Id),
                url: `/ppc/${rec.Id}`,
                type: "Project Page Component",
              });
            }}
          >
            {text || rec.Id}
          </Link>
        ),
      },
      {
        title: "Component Type",
        dataIndex: "RecordType",
        key: "Component Type",
        width: "15%",
        ellipsis: true,
        sorter: (a, b) => a.RecordType - b.RecordType,
        filters: [
          { text: "2 Column 50-50", value: 1 },
          { text: "2 Column Image Left 50-50", value: 2 },
          { text: "2 Column Image Right 50-50", value: 3 },
          { text: "2 Column Image Left 33-66", value: 4 },
          { text: "2 Column Image Right 33-66", value: 5 },
          { text: "3 Column 33-33-33", value: 6 },
          { text: "Headline only", value: 7 },
          { text: "Text Only Full Page", value: 8 },
          { text: "Standalone Button", value: 9 },
          { text: "Image Only", value: 10 },
          { text: "Microsoft Project Page", value: 11 },
          { text: "Splash Page", value: 12 },
        ],
        onFilter: (value, record) => record.RecordType === value,
        render: (_, rec) => {
          const recordType = rec.RecordType;
          const recordTypeMap = {
            1: "2 Column 50-50",
            2: "2 Column Image Left 50-50",
            3: "2 Column Image Right 50-50",
            4: "2 Column Image Left 33-66",
            5: "2 Column Image Right 33-66",
            6: "3 Column 33-33-33",
            7: "Headline only",
            8: "Text Only Full Page",
            9: "Standalone Button",
            10: "Image Only",
            11: "Microsoft Project Page",
            12: "Splash Page",
          };
          return recordTypeMap[recordType] || recordType;
        },
      },
      {
        title: "Project",
        dataIndex: "ProjectName",
        key: "Project",
        width: "25%",
        ellipsis: true,
        sorter: (a, b) => {
          const projectA = a.ProjectName;
          const projectB = b.ProjectName;
          if (!projectA) return -1;
          if (!projectB) return 1;
          return projectA.localeCompare(projectB);
        },
        render: (text, rec) => {
          const project = rec.ProjectName;
          if (!project) return text || rec.ProjectName;
          return text || project.Name;
        },
      },
      {
        title: "Project Page",
        dataIndex: "ProjectPage",
        key: "Project Page",
        width: "10%",
        ellipsis: true,
        sorter: (a, b) => {
          const projectPageA = a.ProjectPage;
          const projectPageB = b.ProjectPage;
          if (!projectPageA) return -1;
          if (!projectPageB) return 1;
          return projectPageA.localeCompare(projectPageB);
        },
        render: (text, rec) => {
          const projectPage = rec.ProjectPage;
          if (!projectPage) return null;
          return text || projectPage.Name;
        },
      },
      {
        title: "Project Page Type",
        dataIndex: "ProjectPageType",
        key: "Project Page Type",
        width: "15%",
        ellipsis: true,
        sorter: (a, b) => {
          const projectPageTypeA = a.ProjectPageType;
          const projectPageTypeB = b.ProjectPageType;
          if (!projectPageTypeA) return -1;
          if (!projectPageTypeB) return 1;
          return projectPageTypeA.localeCompare(projectPageTypeB);
        },
        filters: [
          { text: "Project Page", value: "2 Column 50-50" },
          { text: "Project Objective", value: "Project Objective" },
          { text: "Project Qual Step", value: "Project Qual Step" },
        ],
        onFilter: (value, record) => record.ProjectPageType === value,
        render: (text, rec) => text || rec.ProjectPageType,
      },
      {
        title: "Component Order",
        dataIndex: "Order",
        key: "Component Order",
        width: "10%",
        render: (text, rec) => text || rec.Order,
      },
    ],
    [navigate, record],
  );

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    loadAll();
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="flex items-center justify-between mb-4">
        <Typography.Title level={3} style={{ margin: 0 }}>
          Project Page Components
        </Typography.Title>
        <div className="flex items-center gap-3">
          <Button
            type="primary"
            onClick={() => setShowCreateModal(true)}
            icon={
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            }
          >
            Create
          </Button>
          <Button
            type="default"
            onClick={() => setShowCloneModal(true)}
            icon={<CopyOutlined />}
          >
            Clone Component
          </Button>
          <div className="relative" style={{ maxWidth: 360 }}>
            <Input.Search
              placeholder="Search page components"
              allowClear
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setSearchOpen(false)}
            />
            {searchOpen && query.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
                {searchLoading ? (
                  <div className="p-3 text-sm text-gray-500">
                    <Spin size="small" /> Searching…
                  </div>
                ) : query.length < 3 ? (
                  <div className="p-3 text-sm text-gray-500">
                    Enter at least 3 characters to search
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-3">
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No components found"
                    />
                  </div>
                ) : (
                  <List
                    dataSource={searchResults}
                    size="large"
                    renderItem={(item) => (
                      <List.Item
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        onMouseDown={() => {
                          setQuery("");
                          setSearchResults([]);
                          setSearchOpen(false);
                          record({
                            id: String(item.Id),
                            title: String(item.Name || item.Id),
                            url: `/ppc/${item.Id}`,
                            type: "ProjectPageComponent",
                          });
                          navigate(`/ppc/${item.Id}`);
                        }}
                      >
                        <div className="w-full">
                          <div className="text-gray-900 text-sm font-medium">
                            {item.Name}
                          </div>
                          <div className="text-xs text-gray-500">{item.Id}</div>
                        </div>
                      </List.Item>
                    )}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Clone Page Component Modal */}
      <ClonePageComponentModal
        visible={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        onSuccess={() => {
          setShowCloneModal(false);
          loadAll(); // Refresh the list
        }}
      />
      <Table
        loading={loading}
        rowKey={(r) => r.Id}
        dataSource={records}
        columns={columns}
        scroll={{ y: "calc(100vh - 280px)" }}
        pagination={{
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30", "50", "100"],
          onShowSizeChange: (current, size) => setPageSize(size),
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
      {!loading && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Alert
            type="info"
            message={
              <Typography.Text>
                <strong>NOTE :</strong>
                <> Only first 1000 records are being Displayed.</>
              </Typography.Text>
            }
            icon={<InfoCircleOutlined />}
            style={{ width: "fit-content", alignSelf: "flex-end" }}
            showIcon
          />
        </div>
      )}
      <CreateProjectPageComponentModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
}
